import apiClient from './apiClient'
import cookieUtils from '../utils/cookieUtils'

/**
 * User API Service
 */

const userService = {

    // MARK: UPLOAD USERS

    /** 
     * Upload users via CSV file
     * POST /campaigns/participants/upload
     * 
     * @param {File} file - The CSV file containing user data
     * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
     * @returns {Promise} Response from the server
     */
    uploadUsersCSV: async (file, options = {}) => {
        const response = await apiClient.post(
            '/campaigns/participants/upload',
            file,
            {
                ...options,
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            }
        )
        console.log('uploadUsersCSV response:', response)
        return response.data
    },

    // MARK: ADD USER

    /**
     * Add a new user
     * POST /campaigns/participants
     * 
     * @param {object} userData - The user data to be added
     * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
     * @returns {Promise} Response from the server
     */
    addUser: async (userData, options = {}) => {
        const response = await apiClient.post(
            '/campaigns/participants',
            userData,
            options
        )
        return response.data
    },

    // MARK: UPDATE USER

    /**
     * Update an existing user
     * PUT /campaigns/participants/{userId}
     * 
     * @param {string} userId - The ID of the user to update
     * @param {object} userData - The user data to update
     * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
     * @returns {Promise} Response from the server
     */
    updateUser: async (userId, userData, options = {}) => {
        const response = await apiClient.put(
            `/campaigns/participants/${userId}`,
            userData,
            options
        )
        return response.data
    },

    // MARK: FETCH USERS

    /**
     * Fetch all users     
     * GET /campaigns/participants
     * 
     * @param {number} pageNumber - The page number to fetch (1-based)
     * @param {number} pageSize - The number of items per page
     * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
     * @returns {Promise} List of users
     */
    fetchUsers: async (pageNumber = 1, pageSize = 10, options = {}) => {
        const response = await apiClient.get('/campaigns/participants', {
            ...options,
            params: {
                pagenumber: pageNumber,
                pagesize: pageSize,
            }
        })
        return response.data
    },

    // MARK: UPLOAD FUNDS

    /**
     * Upload funds to issue to users via CSV file
     * POST /campaigns/funds/issue
     * 
     * @param {File} file - The CSV file containing fund data
     * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
     * @returns {Promise} Response from the server
     */
    uploadFundsCSV: async (file, options = {}) => {
        const response = await apiClient.post(
            '/campaigns/funds/issue',
            file,
            {
                ...options,
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            }
        )
        return response.data
    },

    // MARK: ISSUE FUNDS

    /**
     * Issue funds to users
     * POST /campaigns/funds/issue/participants
     * 
     * @param {object} payload - The payload object { amountperparticipant, participants: [{ participantid }] }
     * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
     * @returns {Promise} Response from the server
     */
    issueFunds: async (payload, options = {}) => {
        const response = await apiClient.post(
            '/campaigns/funds/issue/participants',
            payload,
            options
        )
        return response.data
    },

    // MARK: FETCH PARTICIPANTS TEMPLATE

    /**
     * Fetch participants upload template
     * GET /upload/participants/template
     * 
     * @param {object} options - Optional { showSnackbar, showSuccessSnackbar, successMessage, errorMessage }
     * @returns {Promise} Template URL { url: string }
     */
    fetchParticipantsTemplate: async (options = {}) => {
        const response = await apiClient.get(
            '/upload/participants/template',
            options
        )
        return response.data
    }

}

export default userService
